import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AdvancedFilterButton,
  AdvancedFilterPanel,
  FilterPanelCollapse,
} from '@/components/common/AdvancedFilter';
import { DataTable } from '@/components/common/DataTable';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { ErrorState } from '@/components/common/ErrorState';
import { StatCardGrid, type LabeledStat } from '@/components/common/StatCardGrid';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { TAKA_SYMBOL } from '@/constants/app.constants';
import { useGeoCascade } from '@/hooks/useGeoCascade';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { getFilterPanelProps } from '@/hooks/useUrlSyncedFilters';
import { zonalManagersQueryOptions } from '@/features/users/api/users.api';
import {
  getDateFromToFilterFields,
  getGeoFilterFields,
  getZonalManagerFilterField,
} from '@/utils/filterFields';
import { FILTER_KEYS, parseNumberFilter } from '@/utils/filters';
import { allCostInfiniteQueryOptions } from '../api/billing.api';
import { getAllCostColumns } from '../components/AllCostColumns';
import { BILLING_SEARCH_PLACEHOLDER } from '../constants/billing.constants';
import { useAllCostFilters } from '../hooks/useAllCostFilters';

export function AllCostPage() {
  const controller = useAllCostFilters();
  const { rows, firstPageMeta, isLoading, loadError, refetch, loadMoreProps } =
    useInfiniteTableQuery(allCostInfiniteQueryOptions(controller.params));
  const { data: zonalManagers } = useQuery(zonalManagersQueryOptions());

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const geoOptions = useGeoCascade({
    divisionId: parseNumberFilter(controller.filterValues[FILTER_KEYS.divisionId]),
    districtId: parseNumberFilter(controller.filterValues[FILTER_KEYS.districtId]),
  });

  const { search } = controller.params;
  const columns = useMemo(() => getAllCostColumns(search), [search]);

  const totals = firstPageMeta?.totals;
  const stats: LabeledStat[] = [
    {
      key: 'dcrClaimed',
      label: 'DCR Claimed',
      value: totals?.dcrClaimed,
      unit: TAKA_SYMBOL,
    },
    {
      key: 'dcrApproved',
      label: 'DCR Approved',
      value: totals?.dcrApproved,
      unit: TAKA_SYMBOL,
    },
    {
      key: 'expenseClaimed',
      label: 'Expense Claimed',
      value: totals?.expenseClaimed,
      unit: TAKA_SYMBOL,
    },
    {
      key: 'expenseApproved',
      label: 'Expense Approved',
      value: totals?.expenseApproved,
      unit: TAKA_SYMBOL,
    },
    {
      key: 'totalClaimed',
      label: 'Total Claimed',
      value: totals?.totalClaimed,
      unit: TAKA_SYMBOL,
    },
    {
      key: 'totalApproved',
      label: 'Total Approved',
      value: totals?.totalApproved,
      unit: TAKA_SYMBOL,
    },
  ];

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title="View All Cost"
        search={{
          value: controller.searchText,
          onChange: controller.setSearchText,
          placeholder: BILLING_SEARCH_PLACEHOLDER,
        }}
        titleAddon={
          <DateRangeBadge dateFrom={controller.params.dateFrom} dateTo={controller.params.dateTo} />
        }
        actions={
          <AdvancedFilterButton
            isOpen={isFilterOpen}
            activeCount={controller.activeFilterCount}
            onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
          />
        }
      />

      <FilterPanelCollapse isOpen={isFilterOpen}>
        <AdvancedFilterPanel
          fields={[
            ...getDateFromToFilterFields(),
            ...getGeoFilterFields(controller.filterValues, geoOptions),
            getZonalManagerFilterField(zonalManagers),
          ]}
          {...getFilterPanelProps(controller)}
        />
      </FilterPanelCollapse>

      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load the cost"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <>
          <StatCardGrid stats={stats} isLoading={isLoading} />

          <DataTable
            data={rows}
            columns={columns}
            isLoading={isLoading}
            className="lg:flex-1"
            stickyColumnId="name"
            emptyMessage="Nothing was spent in this period"
            emptyHint="Try a wider date range."
            {...loadMoreProps}
          />
        </>
      )}
    </div>
  );
}
